import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '@/presentation/components/common';
import { AccessDenied } from '@/presentation/components/common/AccessDenied';
import { useAuth } from '@/presentation/contexts/AuthContext';
import { useToast } from '@/presentation/contexts/ToastContext';
import { httpClient } from '@/infrastructure/http/client/httpClient';
import {
  DEFAULT_PERIOD_OPS_CONFIG,
  LAST_DAY_OF_MONTH,
  periodOpsTimeValue,
  type PeriodOpsConfig,
} from '@/presentation/components/settings/PeriodOpsConfigForm';

export const PeriodOpsPage: React.FC = () => {
  const { t } = useTranslation();
  const { canAdminister } = useAuth();
  const { success, error: showError } = useToast();
  const [isRunningCierre, setIsRunningCierre] = useState(false);
  const [isRunningPeriodo, setIsRunningPeriodo] = useState(false);
  const [isRunningDeactivate, setIsRunningDeactivate] = useState(false);
  const [lastResult, setLastResult] = useState<string | null>(null);
  const [config, setConfig] = useState<PeriodOpsConfig>(DEFAULT_PERIOD_OPS_CONFIG);

  const loadConfig = useCallback(async () => {
    try {
      const res = await httpClient.get<PeriodOpsConfig>('/panel-config');
      setConfig({ ...DEFAULT_PERIOD_OPS_CONFIG, ...res.data });
    } catch {
      /* las acciones siguen disponibles; el texto de calendario usa defaults */
    }
  }, []);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  if (!canAdminister) {
    return <AccessDenied />;
  }

  const handleCerrarMes = async () => {
    const confirmed = window.confirm(t('pages.periodOps.closeMonthConfirm'));
    if (!confirmed) return;

    setIsRunningCierre(true);
    try {
      const response = await httpClient.post('/panel-pagos/cerrar-mes/run');
      const resultado = response.data.resultado;
      const message = t('pages.periodOps.closeMonthSuccess', {
        periodo: resultado.periodo,
        recaudado: resultado.dineroRecaudado,
      });
      success(message);
      setLastResult(`${new Date().toLocaleString()} — ${message}`);
    } catch {
      showError(t('pages.periodOps.closeMonthError'));
    } finally {
      setIsRunningCierre(false);
    }
  };

  const handleRunPeriodo = async () => {
    const confirmed = window.confirm(t('pages.periodOps.runMonthlyConfirm'));
    if (!confirmed) return;

    setIsRunningPeriodo(true);
    try {
      const response = await httpClient.post('/panel-pagos/periodo/run');
      const cierre = response.data.cierre;
      const message = t('pages.periodOps.runMonthlySuccess', {
        periodo: cierre?.periodo || 'N/A',
      });
      success(message);
      setLastResult(`${new Date().toLocaleString()} — ${message}`);
      void loadConfig();
    } catch {
      showError(t('pages.periodOps.runMonthlyError'));
    } finally {
      setIsRunningPeriodo(false);
    }
  };

  const handleDeactivateClinics = async () => {
    const confirmed = window.confirm(
      t(
        config.desactivarImpagasNotifyWhatsApp
          ? 'pages.periodOps.deactivateClinicsConfirm'
          : 'pages.periodOps.deactivateClinicsConfirmNoWa'
      )
    );
    if (!confirmed) return;

    setIsRunningDeactivate(true);
    try {
      const response = await httpClient.post('/panel-pagos/desactivar-impagas/run', {
        notifyWhatsApp: config.desactivarImpagasNotifyWhatsApp,
      });
      const resultado = response.data.resultado;
      let message = `${t('pages.periodOps.deactivateClinicsSuccess')} ${resultado.clinicasDesactivadas} ${t('pages.periodOps.deactivateClinicsCount')}`;
      if (resultado.notificacionesEnviadas != null || resultado.notificacionesFallidas != null) {
        message += ` ${t('pages.periodOps.deactivateClinicsNotifySummary', {
          sent: resultado.notificacionesEnviadas ?? 0,
          failed: resultado.notificacionesFallidas ?? 0,
        })}`;
      }
      success(message);
      setLastResult(`${new Date().toLocaleString()} — ${message}`);
      void loadConfig();
    } catch {
      showError(t('pages.periodOps.deactivateClinicsError'));
    } finally {
      setIsRunningDeactivate(false);
    }
  };

  const ops = [
    {
      key: 'close',
      title: t('pages.periodOps.closeMonthTitle'),
      description: t('pages.periodOps.closeMonthDescription'),
      border: 'border-amber-500',
      button: (
        <Button variant="secondary" size="sm" onClick={handleCerrarMes} disabled={isRunningCierre} className="w-full sm:w-auto">
          {isRunningCierre ? t('pages.periodOps.closeMonthRunning') : t('pages.periodOps.closeMonthButton')}
        </Button>
      ),
    },
    {
      key: 'run',
      title: t('pages.periodOps.runMonthlyTitle'),
      description: t('pages.periodOps.runMonthlyDescription', {
        day: config.periodoCierreDia,
        time: periodOpsTimeValue(config.periodoCierreHora, config.periodoCierreMinuto),
        status: config.periodoCierreEnabled
          ? t('pages.periodOps.autoOn')
          : t('pages.periodOps.autoOff'),
      }),
      border: 'border-teal-500',
      button: (
        <Button variant="primary" size="sm" onClick={handleRunPeriodo} disabled={isRunningPeriodo} className="w-full sm:w-auto">
          {isRunningPeriodo ? t('pages.periodOps.runMonthlyRunning') : t('pages.periodOps.runMonthlyButton')}
        </Button>
      ),
    },
    {
      key: 'deactivate',
      title: t('pages.periodOps.deactivateClinicsTitle'),
      description: t('pages.periodOps.deactivateClinicsDescription', {
        day:
          config.desactivarImpagasDia === LAST_DAY_OF_MONTH
            ? t('pages.periodOps.lastDayOfMonth')
            : config.desactivarImpagasDia,
        time: periodOpsTimeValue(config.desactivarImpagasHora, config.desactivarImpagasMinuto),
        status: config.desactivarImpagasEnabled
          ? t('pages.periodOps.deactivateAutoOn')
          : t('pages.periodOps.deactivateAutoOff'),
        notify: config.desactivarImpagasNotifyWhatsApp
          ? t('pages.periodOps.notifyOn')
          : t('pages.periodOps.notifyOff'),
      }),
      border: 'border-rose-500',
      button: (
        <Button
          variant="danger"
          size="sm"
          onClick={handleDeactivateClinics}
          disabled={isRunningDeactivate}
          className="w-full sm:w-auto"
        >
          {isRunningDeactivate
            ? t('pages.periodOps.deactivateClinicsRunning')
            : t('pages.periodOps.deactivateClinicsButton')}
        </Button>
      ),
    },
  ];

  return (
    <div className="panel-page">
      <div className="panel-page-header">
        <div className="min-w-0">
          <h1 className="panel-page-title">{t('pages.periodOps.title')}</h1>
          <p className="panel-page-subtitle hidden sm:block">
            {t('pages.periodOps.subtitle')}
          </p>
        </div>
      </div>

      <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3.5 dark:border-amber-800 dark:bg-amber-900/20 sm:px-5 sm:py-4">
        <p className="text-sm leading-relaxed text-amber-800 dark:text-amber-200">
          {t('pages.periodOps.warning')}{' '}
          <Link
            to="/configuracion-periodo"
            className="font-medium text-teal-700 underline underline-offset-2 hover:text-teal-800 dark:text-teal-300 dark:hover:text-teal-200"
          >
            {t('pages.periodOps.goToSettings')}
          </Link>
        </p>
      </div>

      <div className="space-y-3 sm:space-y-3.5">
        {ops.map((op) => (
          <div
            key={op.key}
            className={`panel-list-card border-l-4 px-4 py-4 ${op.border} sm:px-5 sm:py-5`}
          >
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0 sm:pr-4">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                  {op.title}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                  {op.description}
                </p>
              </div>
              <div className="shrink-0">{op.button}</div>
            </div>
          </div>
        ))}
      </div>

      {lastResult && (
        <div className="panel-card px-4 py-4 sm:px-5">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
            {t('pages.periodOps.lastResult')}
          </h3>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{lastResult}</p>
        </div>
      )}
    </div>
  );
};
